export default {
  // Product-phase label — rendered as the in-app "Beta" chip next to the FlowRent wordmark
  beta: 'Beta',
  // Dashboard "needs attention" chips (overdue invoices / expiring leases)
  needs_attention: {
    overdue_cta_one: '{{count}} Overdue Invoice',
    overdue_cta_other: '{{count}} Overdue Invoices',
    overdue_aria_one: '{{count}} overdue invoice, view list',
    overdue_aria_other: '{{count}} overdue invoices, view list',
    expiring_cta_one: '{{count}} Expiring Lease',
    expiring_cta_other: '{{count}} Expiring Leases',
    expiring_aria_one: '{{count}} expiring lease, view list',
    expiring_aria_other: '{{count}} expiring leases, view list',
  },
  // Actions
  actions: {
    add: 'Add',
    edit: 'Edit',
    delete: 'Delete',
    save: 'Save',
    saving: 'Saving…',
    save_changes: 'Save Changes',
    cancel: 'Cancel',
    confirm: 'Confirm',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    close: 'Close',
    switch_language: 'Switch language',
    search: 'Search',
    filter: 'Filter',
    clear: 'Clear',
    retry: 'Retry',
    download: 'Download',
    upload: 'Upload',
    view: 'View',
    create: 'Create',
    select: 'Select',
    update: 'Update',
    remove: 'Remove',
    logout: 'Logout',
    delete_account: 'Delete account',
    collapse: 'Collapse',
    setup: 'Set Up',
    more_actions: 'More actions',
    discard: 'Discard',
    hide: 'Hide',
    show: 'Show',
    leave: 'Leave',
    stay: 'Stay',
    skip_for_now: 'Skip for now',
    why_this_matters: 'Why this matters',
    got_it: 'Got it',
    record_payment: 'Record Payment',
  },

  // Status
  status: {
    active: 'Active',
    inactive: 'Inactive',
    pending: 'Pending',
    completed: 'Completed',
    cancelled: 'Cancelled',
    loading: 'Loading...',
    error: 'Error',
    success: 'Success',
    vacant: 'Vacant',
    occupied: 'Occupied',
    signed: 'Signed',
  },

  // Navigation
  nav: {
    dashboard: 'Dashboard',
    properties: 'Properties',
    customers: 'Customers',
    // EP-111 T111-9 — Co-owner sidebar item, directly below Customers
    // (dual-gate: condominium_enabled only, no property-count requirement).
    // Key renamed from co_owners by EP-133 SS4b-2 (2026-08-20) — the value
    // ('Co-owner') stays exactly as-is; only the internal key moved.
    shared_owners: 'Co-owner',
    // EP-135 Tier 0 — contact directory sub-nav under /co-owners
    shared_owner_directory: 'Contact Directory',
    // EP-135 Tier 1 — Governance (Meetings + Committee). Same gate as the
    // co-owner CRUD list (condominium_enabled only). Hidden for accountant.
    meetings: 'Meetings',
    committee: 'Committee',
    leases: 'Leases',
    co_ownerships: 'Co-Ownerships',
    invoices: 'Invoices',
    maintenance: 'Maintenance',
    parcels: 'Parcels',
    // EP-133 §3c (T133-021) — staff front-desk arrivals view
    visitor_arrivals: 'Visitor Arrivals',
    parking: 'Parking',
    expenses: 'Expenses',
    timesheet: 'Timesheet',
    announcements: 'Announcements',
    reports: 'Reports',
    templates: 'Templates',
    payments: 'Payments',
    meters: 'Meter Readings',
    // EP-111 T111-8 — CAF sidebar item, directly below Meters (dual-gate:
    // condominium_enabled + >=1 condominium-type property).
    caf: 'Common Area Fees',
    parcel_settings: 'Carrier Settings',
    activity_log: 'Activity Log',
    team: 'Team',
    settings: 'Settings',
    cmms: 'CMMS',
    equipment: 'Equipment',
    work_orders: 'Work Orders',
    vendors: 'Vendors',
    admin: 'Admin',
    bank_reconciliation: 'Bank Reconciliation',
    finance: 'Finance',
    operations: 'Operations',
    accounting: 'Accounting',
    chart_of_accounts: 'Chart of Accounts',
    journal_entries: 'Journal Entries',
    financial_statements: 'Financial Statements',
    fixed_assets: 'Fixed Assets',
    debt_collection: 'Debt Collection',
    closing_package: 'Closing Package',
    incentives: 'Incentives',
    incentives_campaigns: 'Campaigns',
    incentives_catalog: 'Reward Catalog',
    incentives_promotions: 'Promotions',
  },

  // Search
  header: {
    resume_onboarding: 'Resume setup',
    resume_onboarding_title: 'You skipped the setup wizard. Click to continue.',
    signed_in_as: 'Signed in as',
    pricing_link: 'Pricing',
  },

  search: {
    placeholder: 'Search anything...',
    no_results: 'No results found',
    recent_searches: 'Recent Searches',
    pages: 'Pages',
    keyboard_hint: 'Cmd+K',
    degraded_mode_banner: 'Search is running in degraded mode (some results may be missing). Service will recover automatically.',
    categories: {
      property: 'Property',
      customer: 'Customer',
      unit: 'Unit',
      lease: 'Lease',
      invoice: 'Invoice',
      maintenance: 'Maintenance',
      parcel: 'Parcel',
      expense: 'Expense',
      payment: 'Payment',
      vehicle: 'Vehicle',
      equipment: 'Equipment',
      work_order: 'Work Order',
      vendor: 'Vendor',
      properties: 'Properties',
      customers: 'Customers',
      units: 'Units',
      leases: 'Leases',
      invoices: 'Invoices',
    },
  },

  // Export
  export_data: {
    title: 'Export Data',
    format: 'Format',
    csv: 'CSV',
    excel: 'Excel (XLSX)',
    date_range: 'Date Range',
    columns: 'Columns',
    select_all: 'Select All',
    progress: 'Exporting... {{progress}}%',
    download: 'Download',
    export_button: 'Export',
    processing: 'Processing...',
    completed: 'Export completed',
    success: 'Exported {{count}} rows',
    failed: 'Export failed',
    billing_period: 'Billing Period',
    all_periods: 'All Periods',
    custom_range: 'Custom Range',
  },

  // Filters
  filters: {
    toggle: 'Filters',
  },

  // Common labels
  labels: {
    image_unavailable: 'Image unavailable',
    name: 'Name',
    email: 'Email',
    phone: 'Phone',
    address: 'Address',
    property: 'Property',
    date: 'Date',
    amount: 'Amount',
    price: 'Price',
    total: 'Total',
    all: 'All',
    all_properties: 'All Properties',
    refreshing: 'Refreshing…',
    loading: 'Loading',
    more_options: 'More options',
    options: 'Options',
    select_property: 'Select property',
    select_property_in_header: 'Use the property switcher in the top bar to choose a property.',
    description: 'Description',
    details: 'Details',
    notes: 'Notes',
    type: 'Type',
    status: 'Status',
    prerequisites: 'Prerequisites',
    created_at: 'Created At',
    updated_at: 'Updated At',
    actions: 'Actions',
    property_reports: 'Property Reports',
    actions_menu: 'Actions',
    settings: 'Settings',
    pre_filled: 'Pre-filled',
    serial: 'Serial',
    unit: 'Unit',
    open_menu: 'Open menu',
    close_menu: 'Close menu',
    close: 'Close',
    copy: 'Copy',
    delete_row: 'Delete row',
    remove_file: 'Remove file',
    toggle_navigation: 'Toggle navigation',
    user_menu: 'User menu',
    clear_search: 'Clear search',
  },

  // Pagination
  pagination: {
    page: 'Page',
    of: 'of',
    showing: 'Showing',
    entries: 'entries',
    previous: 'Previous',
    next: 'Next',
    // {{rows}} is pagination.rows, already pluralised; count is the page count.
    rows_one: '{{count}} row',
    rows_other: '{{count}} rows',
    summary_one: '{{rows}} · {{count}} page · {{perPage}} each',
    summary_other: '{{rows}} · {{count}} pages · {{perPage}} each',
    summary_short_one: '{{rows}} · {{count}} page',
    summary_short_other: '{{rows}} · {{count}} pages',
    go_to_page: 'Go to page',
  },

  // Empty states
  empty: {
    no_data: 'No data found',
    no_results: 'No results found',
  },

  // Errors
  errors: {
    required: 'This field is required',
    invalid_email: 'Invalid email address',
    min_length: 'Must be at least {{count}} characters',
    max_length: 'Must be at most {{count}} characters',
    positive_number: 'Must be a positive number',
    something_went_wrong: 'Something went wrong',
    not_found: 'Not found',
    app_crashed_title: 'Something went wrong',
    app_crashed_body: 'An unexpected error occurred. Please try again or return to the dashboard.',
    app_crashed_retry: 'Try again',
    app_crashed_home: 'Back to dashboard',
    // T149-010 (MON-1 Q2): one payout in the import could not be posted to
    // the ledger, so the whole import was rolled back. Nothing was saved.
    stripe_payout_journal_failed: 'One of the payouts in this file could not be recorded in the ledger. Nothing was imported. Please try again or contact support.',
  },

  // Confirmations
  confirm: {
    delete: 'Are you sure you want to delete this?',
    unsaved_changes: 'You have unsaved changes. Are you sure you want to leave?',
  },

  // Dashboard
  dashboard: {
    // EP-155: one "to do" card per role, the owner's figures, the
    // accountant's closing line. Counts use {{n}} (no plural forms needed).
    todo: {
      title: 'To do',
      nothing: 'Nothing to do',
      load_failed: 'Could not load',
      retry: 'Try again',
      show_list: 'Show list',
      hide_list: 'Hide list',
      view_all: 'View all ({{n}})',
      forgot_one: '{{name}} has not clocked out since {{date}} {{time}}',
      forgot_many: 'Not clocked out: {{n}} people',
      forgot_action: 'Fix',
      slips: 'Slips to review: {{n}}',
      slips_action: 'Review slips',
      parking_unpaid: 'Unpaid parking {{n}}',
      parking_unpaid_action: 'View',
      expenses_confirm: 'Expenses to confirm: {{n}}',
      expenses_confirm_action: 'Confirm expenses',
      unidentified: 'Payments with no payer named: {{n}}',
      unidentified_action: 'Name payer',
      bank: 'Bank rows not matched: {{n}}',
      bank_action: 'Reconcile',
      receipts: 'Expenses without a receipt: {{n}}',
      receipts_action: 'View expenses',
      not_billed: 'Not billed yet for {{period}}',
      not_billed_action: 'Generate invoices',
      overdue: 'Overdue invoices: {{n}}  {{amount}}',
      overdue_action: 'Follow up',
      expiring: 'Leases ending within 30 days: {{n}}',
      expiring_action: 'View leases',
      ends: 'ends {{date}}',
      expired: 'Leases expired, not renewed: {{n}} (rent is no longer billed)',
      expired_action: 'Renew or end',
      expired_since_one: 'ended {{date}} ({{count}} day ago)',
      expired_since_other: 'ended {{date}} ({{count}} days ago)',
      superseded: 'New lease exists, close the old one: {{n}}',
      superseded_action: 'End the old lease',
      superseded_newer: 'ended {{date}}  new lease {{code}}',
      meters: 'Meters not read this month: {{n}}',
      meters_action: 'Read meters',
      meter_record: 'Record',
      parcels: 'Parcels waiting: {{n}}',
      parcels_action: 'View parcels',
      maintenance: 'Open repairs: {{n}}',
      maintenance_action: 'View repairs',
    },
    money: {
      period: 'Period {{period}}',
      billed: 'Billed',
      received: 'Received',
      received_rate: '{{rate}}% of billed',
      unpaid: 'Unpaid',
      net_profit: 'Net profit',
      occupancy: 'Occupancy',
      rent_roll: 'Rent roll / month',
      vacancy_gap: 'Lost to vacancy',
      deposits_held: 'Deposits held',
    },
    closing: {
      title: 'Close {{month}}',
      open: 'Open items: {{n}}',
      clear: 'Nothing open',
      wht: 'Withholding tax: {{n}}',
      action: 'Prepare closing package',
    },
    no_building: 'There is no building in this account yet. The account owner sets it up.',
    open_maintenance: 'Open maintenance requests',
    waiting_parcels: 'Parcels awaiting collection',
    // EP-80: mobile QuickActionBar labels (staff-only bottom nav)
    // T003: record_payment, add_meter_reading, add_parcel added for staff ops CTAs
    // EP-154: the clock card at the top of the staff and admin dashboard.
    shift: {
      duration: '{{hours}} h {{minutes}} min',
      clock_in: 'Clock in',
      clock_out: 'Clock out',
      saving: 'Saving…',
      locating: 'Finding your location…',
      tap_to_cancel: 'Tap to cancel',
      cannot_clock_in: 'Cannot clock in',
      cannot_clock_out: 'Cannot clock out',
      issue_location: 'Location permission is off',
      errors: {
        shift_already_open: 'A shift is already open. Clock out first.',
        no_open_shift: 'No shift is open.',
        shift_too_long: 'This shift passed 24 hours. Close it on the timesheet with the real end time.',
        shift_too_short: 'Clock out a moment after clocking in.',
        clock_proof_required: 'A selfie and your location are both needed.',
        no_position: 'Could not read your location. Try again',
        forbidden: 'This week is locked. Ask an admin.',
        failed: 'Could not record. Try again.',
      },
    },
    // EP-154: who is clocked in now, on the admin dashboard.
    team_today: {
      title: 'Team today',
      not_started: 'Not clocked in',
      since: 'since {{time}}',
      open_since: 'open since {{date}} {{time}}',
      fix: 'Fix',
      view_timesheet: 'View timesheet',
      in_count: '{{present}} of {{total}} in',
      show: 'Show list',
      hide: 'Hide list',
      forgot_clock_out: '{{name}} clocked in {{date}} {{time}} and has not clocked out',
    },
    quick_actions: {
      // The staff bottom bar: five labels that fit one line each on a phone.
      bar: {
        record_payment: 'Payment',
        new_invoice: 'Invoice',
        add_reading: 'Reading',
        log_maintenance: 'Repair',
        quick_add: 'Scan',
      },
      new_invoice: 'New Invoice',
      add_reading: 'Add Reading',
      log_maintenance: 'Log Maintenance',
      quick_add: 'Quick add',
      record_payment: 'Record Payment',
      add_meter_reading: 'Add Reading',
      add_parcel: 'Add Parcel',
    },
    title: 'Dashboard',
    welcome: 'Welcome back, {{name}}!',
    welcome_short: 'Hi, {{name}}',
    total_properties: 'Total Properties',
    total_units: 'Total Units',
    active_leases: 'Active Leases',
    pending_invoices: 'Pending Invoices',
    occupied_units: 'Occupied Units',
    vacant_units: 'Vacant Units',
    occupancy_rate: 'Occupancy Rate',
    overdue_invoices: 'Overdue Invoices',
    revenue_this_month: 'Revenue This Month',
    billed_this_month: 'Billed This Month',
    outstanding_balance: 'Outstanding Balance',
    overdue_amount: 'Overdue Amount',
    recent_invoices: 'Recent Invoices',
    recent_payments: 'Recent Payments',
    view_all: 'View All',
    expiring_leases: 'Expiring Leases',
    expiring_leases_notice: '{{count}} lease(s) expiring within 30 days',
    overdue_notice: '{{count}} invoice(s) overdue, totaling ฿{{amount}}',
    overdue_widget_title: 'Overdue Invoices',
    overdue_widget_empty: 'No overdue invoices',
    overdue_days_late_one: '{{count}} day late',
    overdue_days_late_other: '{{count}} days late',
    no_recent_invoices: 'No recent invoices',
    no_recent_payments: 'No recent payments',
    no_expiring_leases: 'No expiring leases',
    logged_in_as: 'You are logged in as',
    for_account: 'for',
    setup_guide_title: 'Welcome to FlowRent!',
    setup_guide_description: 'Get started by setting up your first property.',
    setup_step_1_title: 'Create a property',
    setup_step_1_description: 'Add your apartment or building with room types and units.',
    setup_step_2_title: 'Add customers',
    setup_step_2_description: 'Register your tenants and create lease agreements.',
    setup_step_3_title: 'Start billing',
    setup_step_3_description: 'Generate invoices and track payments.',
    setup_action: 'Create Property',
    revenue_overview: 'Revenue Overview',
    key_stats: 'Key Stats',
    today_activity: "Today's Activity",
    no_activity: 'No recent activity',
    meter_readings_progress: 'Meter Readings (Current Month)',
    payments_today: 'Payments (Today)',
    meter_readings_due: 'Meter Readings Due',
    no_meters: 'No meters found for this property',
    my_units: 'My Units',
    onboarding_complete: {
      row_link: 'Re-run this step',
    },
    onboarding_in_progress: {
      row_link: 'Continue',
    },
    // EP-109: horizontal card carousel — constant title + count-pill progress
    // badge, neutral-tense step copy (replaces the past-tense onboarding_complete
    // row copy above, which is now consumed by nothing).
    onboarding: {
      title: 'Set up FlowRent to get started',
      progress: '{{done}}/{{total}} complete',
      step_1_title: 'Create a property',
      step_1_description: "Add your property's name, address, and unit count.",
      step_2_title: 'Set pricing',
      step_2_description: 'Set the rent formula and accepted payment methods.',
      step_3_title: 'Add rooms',
      step_3_description: 'Add the rooms or units available for rent.',
      step_4_title: 'Add a customer',
      step_4_description: "Add your first customer's name and contact info.",
      step_5_title: 'Create a lease',
      step_5_description: 'Create a lease linking a customer to a room.',
      step_6_title: 'Record meter readings',
      step_6_description: 'Record the water and electricity meter readings.',
      step_7_title: 'Generate an invoice',
      step_7_description: 'Generate an invoice from the lease and meter readings.',
      step_8_title: 'Record a payment',
      step_8_description: 'Record a payment received against the invoice.',
      cta_done: 'Done',
      cta_add: 'Add',
    },
    // RPT-4 — BusinessSnapshot + owner dashboard slimming
    business_snapshot_title: 'Business Snapshot',
    view_all_reports: 'View reports',
    finish_setup_discover_title: 'Finish setup & discover',
    discover_dismiss: 'Dismiss',
    discover_payment_method: 'Set up a payment method',
    discover_carrier: 'Add a delivery carrier',
    discover_line_oa: 'Connect LINE Official Account',
    discover_invite_team: 'Invite a team member',
    discover_try_feature: 'Try {{feature}} (unused)',
    // T129-010: accountant dashboard widget labels
    accountant: {
      payments_pending: 'Payments Awaiting Review',
      unmatched_bank_rows: 'Unmatched Bank Rows',
      missing_receipts: 'Expenses Missing Receipts',
      wht_expenses: 'WHT Expenses This Period',
      current_period: 'Current Period',
      closing_package: 'Closing Package',
    },
  },

  // T129-012: closing package page labels
  closing_package: {
    title: 'Closing Package',
    month_picker_label: 'Period',
    completeness_title: 'Completeness Checklist',
    download_button: 'Download Package',
    downloading: 'Assembling…',
    status_ok: 'OK',
    status_warning: 'Needs Attention',
    checklist_invoices: 'Invoices',
    checklist_payments: 'Payments',
    checklist_expenses: 'Expenses',
    checklist_pending_expenses: 'Transfers out awaiting an expense',
    checklist_missing_receipts: 'Expenses Missing Receipts',
    checklist_unmatched_bank: 'Unmatched Bank Rows',
    checklist_bank_statement: 'Bank statements',
    bank_coverage_none: 'No bank statement for this month yet',
    bank_coverage_partial: 'Bank statements cover through {{date}}',
    checklist_pending_verification: 'Payments Pending Verification',
    checklist_unidentified_payments: 'Unidentified Payments',
    warnings_note: 'Some items need attention but download is always available.',
    manifest_period: 'Period',
    manifest_counts: 'Record Counts',
    manifest_warnings: 'Warnings',
  },

  // Navigation
  // (nav.closing_package added here for Sidebar entry)

  // Currency
  currency: {
    thb: '฿',
    per_month: '/month',
  },

  // Breadcrumbs
  breadcrumbs: {
    home: 'Home',
  },

  // File upload widget
  file_upload: {
    click_to_upload: 'Click to upload',
    or_drag_drop: 'or drag and drop',
    uploading: 'Uploading…',
    files_counter: '{{count}}/{{max}} files',
    error: 'Upload failed',
  },

  // Sort controls (EP-97) — shared across all list pages
  sort: {
    label: 'Sort by',
    order_asc: 'Ascending',
    order_desc: 'Descending',
    by_newest: 'Newest first',
    by_oldest: 'Oldest first',
    by_name: 'Name',
    by_unit: 'Unit / Room',
    by_due_date: 'Due date',
    by_most_overdue: 'Most overdue',
    by_amount: 'Amount',
    by_status: 'Status',
    by_priority: 'Priority',
    by_rating: 'Rating',
    by_expiring: 'Expiring soonest',
    by_transaction_date: 'Transaction date',
    by_plate: 'License plate',
    by_last_updated: 'Last updated',
    // EP-111 T111-9 — co-owner list sort option (started_at).
    by_start_date: 'Start date',
  },

  // List-page affordance strings (EP-97)
  list: {
    showing_period: 'Showing {{period}}',
    all_periods: 'All periods',
    showing_filtered: 'Showing a filtered view',
    show_all: 'Show all',
    total_count: '{{count}} total',
  },

  // F4: Pricing formula shared keys (consumed by Property, Unit, and Invoice editors)
  pricing_formula: {
    types: {
      discount: 'Discount',
    },
    discount_amount_placeholder: '20',
    discount_label_placeholder: 'Loyalty discount',
    discount_label_aria: 'Discount label',
    discount_amount_aria: 'Discount amount',
    // Fallback label when operator leaves the label field blank (used by F3 + F5)
    default_discount_label: 'Discount',
    discount_hint: 'Reduces invoice total. Stacks additively across property, unit, and per-invoice levels. There is no cap, so it is an owner/admin responsibility. Tip: use distinct labels. Multiple blank-label discounts are auto-deduped (Discount, Discount_2…) but read identically on the invoice unless labeled.',
    negative_total_warning: 'Total will be ≤ 0 with current discounts. Confirm this is intended.',
  },

  // Shared SignaturePad control (lease e-sign) — no namespace of its own, lives in common
  signature_pad: {
    clear: 'Clear',
    confirm_signature: 'Confirm Signature',
    draw_hint: 'Draw your signature in the box above',
    captured: 'Signature captured',
  },

  // Shared DateInput control (EP-149 T149-090, PAY-4/DAT-2), no namespace of its own, lives in common
  date_input: {
    order_dd_mm_yyyy: 'DD/MM/YYYY',
    order_mm_dd_yyyy: 'MM/DD/YYYY',
    order_yyyy_mm_dd: 'YYYY-MM-DD',
    buddhist_conversion: 'Converts BE {{from}} to CE {{to}}',
    out_of_range: 'Date must be between {{min}} and {{max}}',
    // unboundedMax (EP-149 LSE-1): the field has no upper bound, so the
    // caption states only the floor.
    out_of_range_min: 'Date must be on or after {{min}}',
  },

  // EP-160 section 2.3: a FlowRent support session tab (support's browser only)
  support_session: {
    starting: 'Opening the support session',
    expired_title: 'Session expired',
    expired_body: 'This support session timed out or was closed. Close this tab and open a new session from the FlowRent support console.',
    ended_title: 'Session ended',
    ended_body: 'This support session has ended. You can close this tab.',
    invalid_link_title: 'This session link does not work',
    invalid_link_body: 'The link was already used or has expired. It works once, within 10 minutes. Open a new session from the FlowRent support console.',
    // EP-160 S3: the banner of a support tab, the hidden fields and the refusals.
    banner: {
      label: 'FlowRent team support session',
      account: 'Account {{name}}',
      team: 'FlowRent team ({{name}})',
      scope: 'Scope: {{label}}',
      time_left: '{{time}} h left',
      end: 'End session',
    },
    scopes: {
      read: 'Look only',
      setup: 'Set up for me',
      tenants: 'Set up and enter my tenants',
    },
    hidden: '[ hidden ]',
    hidden_description: 'Not shown in a FlowRent team session',
    lease_pdf: 'Lease PDF',
    field_locked_hint: 'Masked in a FlowRent team session, so it cannot be changed',
    setup_no_tenants_notice: 'The "Set up for me" scope cannot enter or change tenant details. The account owner enters them, or grants "Set up and enter my tenants".',
    import_setup_notice: 'The "Set up for me" scope imports only the building, pricing and rooms tabs. The account owner confirms the tenants and leases tabs later.',
    import_setup_tenant_rows: 'This file holds tenants or leases, so edits in the grid are not saved. Each tab is imported as read from the file.',
    blocked_title: 'This page is not open to the FlowRent team in a support session',
    blocked_body: 'The account owner can open it when signed in.',
    blocked_home: 'Back to the dashboard',
    errors: {
      support_session_blocked: 'This page is not open to the FlowRent team in a support session',
      search_blocked: 'This scope cannot search by name or phone. Search by room number or lease code.',
      masked_value_in_write: 'Not saved: it holds a value masked in the FlowRent team session',
      scope_setup_no_tenants: 'Not saved: the "Set up for me" scope cannot enter or change tenant details',
    },
  },
}
